'use client';
import { Badge } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthQueryOptions } from '@/api';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';

import { TYPOGRAPHY_IMAGES } from '../constants';
import { MyGiftErrorFallback } from './ErrorFallback';
import { StampButton } from './StampButton';

type GiftItemProps = {
  completedDate?: string;
  deliveryCompletedYN: boolean;
  deliveryDate?: string;
  institutionLogoUrl: string;
  institutionName: string;
  mainImageUrl: string;
  paidDate: string;
  productName: string;
};

const CheckIcon = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M2.5 6.5L4.5 8.5L9.5 3.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepIcon = ({ active }: { active: boolean }) => (
  <div
    className={cn(
      'absolute inset-0 rounded-pill border-2 border-Gray-white flex-center',
      active ? 'bg-O-500' : 'bg-Gray-200',
    )}
  >
    {active && CheckIcon}
  </div>
);

const GiftItem = ({
  deliveryCompletedYN,
  institutionLogoUrl,
  institutionName,
  mainImageUrl,
  productName,
  paidDate,
  deliveryDate,
  completedDate,
}: GiftItemProps) => {
  const status = deliveryCompletedYN
    ? 'complete'
    : deliveryDate
      ? 'shipping'
      : 'waiting';

  const isComplete = status === 'complete';
  const isShipping = status === 'shipping';
  const isActive = isComplete || isShipping;

  const badgeConfig = {
    waiting: { text: '기부완료', style: 'bg-Gray-200 text-Gray-700' },
    shipping: { text: '배송중', style: 'bg-O-25 text-O-300' },
    complete: { text: '배송완료', style: 'bg-O-400 text-Gray-white' },
  }[status];

  return (
    <motion.article
      initial={{ x: -20 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex w-full flex-col gap-12 rounded-large bg-Gray-white p-16"
      role="listitem"
      aria-label={`${institutionName} ${productName}, ${badgeConfig.text}`}
    >
      <div className="relative flex w-full items-center gap-16">
        <Image
          src={mainImageUrl}
          alt={productName}
          width={240}
          height={240}
          className="size-80 shrink-0 rounded-large border border-[rgba(0,0,0,0.05)] object-cover"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex items-center gap-4">
            <Image
              src={institutionLogoUrl}
              alt={institutionName}
              width={60}
              height={60}
              className="size-20 shrink-0 rounded-pill border border-Gray-300 object-cover"
            />
            <p className="font-600 text-Gray-600 typo-caption1">
              {institutionName}
            </p>
          </div>
          <p className="truncate font-500 text-Gray-900 typo-body4">
            {productName}
          </p>
        </div>

        <Badge className={cn('absolute right-0 top-0', badgeConfig.style)}>
          {badgeConfig.text}
        </Badge>
      </div>

      <div className="relative h-60 w-full">
        <div className="absolute left-23 right-26 top-10 h-4 rounded-xxsmall bg-Gray-200" />
        <div
          className="absolute left-23 top-10 h-4 rounded-xxsmall bg-O-200 transition-all duration-500"
          style={{
            width: isComplete
              ? 'calc(100% - 49px)'
              : isShipping
                ? 'calc(50% - 23px)'
                : '0px',
          }}
        />

        <div className="absolute left-13 top-2">
          <div className="flex flex-col items-center gap-4">
            <div className="relative size-20">
              <StepIcon active />
            </div>
            <div className="flex flex-col items-center whitespace-nowrap">
              <p className="font-700 text-O-500 typo-caption1">기부완료</p>
              <p className="font-600 text-O-500 typo-caption1">
                {dayjs(paidDate).format('MM.DD') || '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="absolute left-1/2 top-2 -translate-x-1/2">
          <div className="flex flex-col items-center gap-4">
            <div className="relative size-20">
              <StepIcon active={isActive} />
            </div>
            <div className="flex flex-col items-center whitespace-nowrap">
              <p
                className={cn(
                  'font-700 typo-caption1',
                  isActive ? 'text-O-500' : 'text-Gray-550',
                )}
              >
                배송중
              </p>
              {isActive && (
                <p className="font-600 text-O-500 typo-caption1">
                  {dayjs(deliveryDate).format('MM.DD') || '-'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="absolute right-16 top-2">
          <div className="flex flex-col items-center gap-4">
            <div className="relative size-20">
              <StepIcon active={isComplete} />
            </div>
            <div className="flex flex-col items-center whitespace-nowrap">
              <p
                className={cn(
                  'font-700 typo-caption1',
                  isComplete ? 'text-O-500' : 'text-Gray-550',
                )}
              >
                배송완료
              </p>
              {isComplete && (
                <p className="font-600 text-O-500 typo-caption1">
                  {dayjs(completedDate).format('MM.DD') || '-'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const MyGiftContent = () => {
  const headerRef = useRef(null);
  const listRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: '-80px' });
  const isListInView = useInView(listRef, { once: true, margin: '-50px' });
  const isLogin = useAuthStore((state) => !!state.isLogin);

  const { data: localTourData } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home-donation/event/local-tour'](),
    enabled: isLogin,
    select: ({ context }) => context,
  });

  const gifts = localTourData?.localTourGiftOrderList ?? [];

  const isEmpty = gifts.length === 0;

  return (
    <section
      className="flex w-full flex-col items-center gap-20 bg-Y-50 px-20 pb-60 pt-20"
      aria-labelledby="my-gift-heading"
    >
      <motion.div
        ref={headerRef}
        initial={{ opacity: 0, y: 20 }}
        animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex flex-col items-center"
      >
        <div className="relative mb-30 h-85 w-125" aria-hidden="true">
          <Lottie src="/assets/jsons/truck.json" />
        </div>
        <h2 id="my-gift-heading">
          <Image
            src={TYPOGRAPHY_IMAGES.myGiftHeading.url}
            alt={TYPOGRAPHY_IMAGES.myGiftHeading.alt}
            width={TYPOGRAPHY_IMAGES.myGiftHeading.width}
            height={TYPOGRAPHY_IMAGES.myGiftHeading.height}
            loading="lazy"
            className="mx-auto object-contain"
          />
        </h2>
      </motion.div>

      {isEmpty ? (
        <StampButton
          variant="orange"
          href={`${DONATION_ROUTES.GIFTS.pathname}?utm_source=wello&utm_medium=event&utm_campaign=wellotour_gift&utm_content=donation_251106`}
        >
          기부하러 가기
        </StampButton>
      ) : (
        <div className="flex w-full flex-col gap-80">
          <motion.div
            ref={listRef}
            initial={{ y: 30 }}
            animate={isListInView ? { y: 0 } : { y: 30 }}
            transition={{
              duration: 0.6,
              delay: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="flex w-full flex-col gap-12"
            role="list"
            aria-label="답례품 목록"
          >
            {gifts.map((gift, index) => (
              <GiftItem key={index} {...gift} />
            ))}
          </motion.div>
          <StampButton
            variant="orange"
            href={`${DONATION_ROUTES.GIFTS.pathname}?utm_source=wello&utm_medium=event&utm_campaign=wellotour_gift&utm_content=donation_251106`}
          >
            답례품 더 받기
          </StampButton>
        </div>
      )}
    </section>
  );
};

export const MyGift = () => {
  return (
    <ErrorBoundary FallbackComponent={MyGiftErrorFallback}>
      <MyGiftContent />
    </ErrorBoundary>
  );
};
